import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { submitSelfConsentThunk, rejectConsentThunk } from "../features/consent/consentThunk";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import { useNavigate } from "react-router-dom";

const ConsentDetails: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { loading, submitted, rejected } = useAppSelector((s) => s.consent);
    const { linkedAccounts, selectedAccountsToLink } = useAppSelector((s) => s.account);

    const [showBottomSheet, setShowBottomSheet] = React.useState(false);
    const accountsToDisplay = selectedAccountsToLink.length > 0 ? selectedAccountsToLink : linkedAccounts;
    const hasAccountsToShare = accountsToDisplay.length > 0;

    // For Self Consent, we don't fetch an FIU handle. We set up the consent parameters directly.
    const consent = {
        PURPOSE: "Personal Finance Management",
        FREQUENCY: "1 Hour",
        DATA_RANGE_FROM: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
        DATA_RANGE_TO: new Date().toISOString(),
        FI_TYPES: ["DEPOSIT", "TERM_DEPOSIT", "MUTUAL_FUNDS", "EQUITIES"]
    };

    useEffect(() => {
        if (submitted) {
            // Success - hand off to the final Success & Redirect screen
            navigate("/successRedirect");
        }
        if (rejected) {
            navigate("/consentDeny");
        }
    }, [submitted, rejected, navigate]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-fin-bg">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-fin-500 mb-4"></div>
                <p className="text-fin-slate font-medium animate-pulse">Fetching consent details...</p>
            </div>
        );
    }

    // Removed consent fetch error checks since we use static self consent

    return (
        <div className="flex flex-col min-h-screen bg-fin-bg pb-24 relative">
            <div className="p-6 max-w-sm mx-auto w-full flex-1">

                <div className="mb-6 mt-4">
                    <h1 className="text-xl font-bold text-white leading-snug">
                        Requester requires your account data for the following consents
                    </h1>
                </div>

                <div className="space-y-4">
                    <Card className="border border-fin-border shadow-none bg-fin-surface">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="font-bold text-white text-base">{consent.PURPOSE}</h3>
                                <p className="text-xs text-fin-primary font-semibold mt-1 bg-fin-primary/10 inline-block px-2 py-0.5 rounded-full uppercase tracking-wide">
                                    {consent.FREQUENCY || 'ONE TIME'}
                                </p>
                            </div>
                        </div>

                        <div className="space-y-3 bg-fin-bg p-4 rounded-xl border border-fin-border">
                            <div className="flex justify-between">
                                <span className="text-xs text-fin-slate font-medium">Data Date Range</span>
                                <span className="text-xs font-semibold text-white">
                                    {consent.DATA_RANGE_FROM ? formatDate(consent.DATA_RANGE_FROM) : 'N/A'} - {consent.DATA_RANGE_TO ? formatDate(consent.DATA_RANGE_TO) : 'N/A'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-xs text-fin-slate font-medium">Consent Validity</span>
                                <span className="text-xs font-semibold text-white">
                                    Until {consent.DATA_RANGE_TO ? formatDate(consent.DATA_RANGE_TO) : 'Revoked'}
                                </span>
                            </div>
                            <div className="flex justify-between items-start">
                                <span className="text-xs text-fin-slate font-medium mt-0.5">Account Types</span>
                                <div className="text-right flex flex-col items-end gap-1">
                                    {consent.FI_TYPES.map((type) => (
                                        <span key={type} className="text-[10px] font-bold text-fin-slate bg-fin-surface border border-fin-border px-1.5 py-0.5 rounded shadow-sm">
                                            {type}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {hasAccountsToShare && (
                    <div className="mt-8">
                        <h3 className="text-sm font-bold text-white mb-3">Accounts to be shared</h3>
                        <div className="space-y-2">
                            {accountsToDisplay.map((acc, idx) => (
                                <div key={idx} className="flex justify-between items-center bg-fin-surface border border-fin-border p-3 rounded-lg">
                                    <div>
                                        <span className="text-xs text-fin-slate font-bold uppercase block mb-0.5">{acc.ACC_TYPE}</span>
                                        <span className="font-mono font-bold text-white text-sm tracking-tight">{acc.MASKED_ACC_NO || 'xxxx'}</span>
                                    </div>
                                    <span className="text-[10px] bg-fin-primary/20 text-fin-primary font-bold px-2 py-1 rounded-md uppercase tracking-wider border border-fin-primary/30">Selected</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Sticky Action Footer */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-fin-surface border-t border-fin-border shadow-none z-20">
                <div className="max-w-sm mx-auto flex gap-3">
                    <Button
                        variant="ghost"
                        className="flex-[1] text-red-500 hover:bg-red-900/10 bg-fin-surface border border-red-900/20 font-semibold"
                        onClick={() => dispatch(rejectConsentThunk({ consentHandle: "SELF_CONSENT", reasonCode: "USER_DENIED" }))}
                    >
                        Deny
                    </Button>
                    <Button
                        variant="primary"
                        className="flex-[2] py-3.5"
                        onClick={() => setShowBottomSheet(true)}
                        disabled={!hasAccountsToShare}
                    >
                        {hasAccountsToShare ? "Review & Approve" : "Select Accounts First"}
                    </Button>
                </div>
            </div>

            {/* Screen 7: Bottom Sheet (Simulated) */}
            {showBottomSheet && (
                <>
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
                        onClick={() => setShowBottomSheet(false)}
                    />
                    <div className="fixed bottom-0 left-0 right-0 mx-auto max-w-sm bg-fin-surface rounded-t-3xl shadow-2xl z-50 transform transition-transform duration-300 ease-out p-6 pt-2 max-h-[90vh] overflow-y-auto border-t border-fin-border">
                        <div className="w-12 h-1.5 bg-fin-border rounded-full mx-auto my-3" />

                        <div className="mb-6 text-center">
                            <div className="w-16 h-16 bg-fin-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-fin-primary/20">
                                <svg className="w-8 h-8 text-fin-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-white">Approve Consent</h2>
                            <p className="text-sm text-fin-slate mt-2 px-4 leading-relaxed">
                                You are about to securely share your financial data for <strong className="text-white">{consent.PURPOSE}</strong>.
                            </p>
                        </div>

                        <div className="bg-fin-bg p-4 rounded-xl border border-fin-border mb-6 space-y-3">
                            <div className="flex justify-between items-center pb-3 border-b border-fin-border">
                                <span className="text-xs text-fin-slate font-semibold uppercase">Frequency</span>
                                <span className="text-sm font-bold text-fin-primary bg-fin-primary/10 px-2 py-0.5 rounded">{consent.FREQUENCY || 'ONE TIME'}</span>
                            </div>
                            <div className="flex justify-between items-center pt-1">
                                <span className="text-xs text-fin-slate font-semibold uppercase">Expiry</span>
                                <span className="text-sm font-bold text-white bg-fin-surface px-2 py-0.5 rounded border border-fin-border cursor-not-allowed">
                                    {consent.DATA_RANGE_TO ? formatDate(consent.DATA_RANGE_TO) : 'N/A'}
                                </span>
                            </div>
                        </div>

                        <p className="text-[11px] text-fin-slate text-center mb-6 px-4">
                            By clicking Approve, you authorize the Account Aggregator to fetch and securely transfer data from your linked accounts.
                        </p>

                        <div className="flex gap-3 pb-4">
                            <Button
                                variant="outline"
                                className="flex-1 py-3.5 border-fin-border text-fin-slate hover:bg-fin-bg"
                                onClick={() => setShowBottomSheet(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="flex-[2] py-3.5"
                                onClick={() => {
                                    setShowBottomSheet(false);
                                    dispatch(submitSelfConsentThunk());
                                }}
                            >
                                Approve & Share
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ConsentDetails;
